const ON_SQUAD_STATUSES = ["approved", "held"];

export const signupPlacement = (signup) => {
  const status = signup?.status;
  if (status === "waitlisted") return "waitlist";
  if (status === "cancelled") return "cancelled";
  if (status === "denied") return "denied";
  if (ON_SQUAD_STATUSES.includes(status)) return "on_squad";
  return "on_squad";
};

export const signupMoney = (signup) => {
  const payment = signup?.paymentStatus || "unpaid";
  if (payment === "refunded") return "refunded";
  if (signupPlacement(signup) !== "on_squad") return null;
  if (payment === "paid") return "paid";
  if (payment === "walk_up") return "walk_up";
  if (payment === "held") return "held";
  return "due";
};

export const placementLabel = (signup) => {
  const placement = signupPlacement(signup);
  if (placement === "waitlist") return "Waitlist";
  if (placement === "cancelled") return "Cancelled";
  if (placement === "denied") return "Denied";
  return "On squad";
};

export const bowlingCenterMapsHref = (center) => {
  const address = String(center?.formattedAddress || "").trim();
  if (!address) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    address
  )}`;
};

export const eventEntryFee = (event) => {
  const fees = event?.eventFee;
  if (Array.isArray(fees) && fees.length) {
    return fees.reduce((sum, fee) => sum + Number(fee.amount || 0), 0);
  }
  return Number(event?.entryFee || 0);
};

export const signupAdvertisedTotal = (signup) => {
  const bowlers = signup?.signupBowlers || signup?.bowlers || [];
  const bowlerCount = Math.max(bowlers.length, 1);
  const entry = eventEntryFee(signup?.tournamentEvents) * bowlerCount;
  const extras = bowlers.reduce(
    (sum, bowler) =>
      sum +
      Number(bowler.totalSidepotCost || 0) +
      Number(bowler.totalBracketCost || 0),
    0
  );
  return entry + extras;
};

const formatUsd = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Number(amount) || 0);

export const moneyLabel = (signup) => {
  const money = signupMoney(signup);
  const disputed = signup?.disputed ? " · Disputed" : "";
  if (money === "paid") return `Paid${disputed}`;
  if (money === "walk_up") return `Paid${disputed}`;
  if (money === "due") {
    const total = signupAdvertisedTotal(signup);
    return total > 0 ? `Due ${formatUsd(total)}` : "Due";
  }
  if (money === "held") return "Held";
  if (money === "refunded") {
    const total = signupAdvertisedTotal(signup);
    return total > 0 ? `Refunded ${formatUsd(total)}` : "Refunded";
  }
  return null;
};

export const hasOpenRefundRequest = (signup) =>
  Boolean(signup?.refundRequested || signup?.refundRequestStatus === "open");

export const matchesDashboardFilter = (signup, filter) => {
  if (!filter || filter === "all") return true;
  if (filter === "on_squad") return signupPlacement(signup) === "on_squad";
  if (filter === "waitlist") return signupPlacement(signup) === "waitlist";
  if (filter === "cancelled") return signup?.status === "cancelled";
  if (filter === "due") {
    const money = signupMoney(signup);
    return money === "due" || money === "held";
  }
  if (filter === "paid") {
    const money = signupMoney(signup);
    return money === "paid" || money === "walk_up";
  }
  return true;
};

export const dashboardCounts = (signups = []) => ({
  all: signups.length,
  on_squad: signups.filter((signup) => signupPlacement(signup) === "on_squad")
    .length,
  waitlist: signups.filter((signup) => signupPlacement(signup) === "waitlist")
    .length,
  due: signups.filter((signup) => {
    const money = signupMoney(signup);
    return money === "due" || money === "held";
  }).length,
  paid: signups.filter((signup) => {
    const money = signupMoney(signup);
    return money === "paid" || money === "walk_up";
  }).length,
  cancelled: signups.filter((signup) => signup.status === "cancelled").length,
});

export const dueTotal = (signups = []) =>
  signups
    .filter((signup) => {
      const money = signupMoney(signup);
      return money === "due" || money === "held";
    })
    .reduce((sum, signup) => sum + signupAdvertisedTotal(signup), 0);

export const needsAttentionCount = (signups = []) =>
  signups.filter(
    (signup) =>
      signupMoney(signup) === "due" ||
      signupMoney(signup) === "held" ||
      signupPlacement(signup) === "waitlist" ||
      signup?.disputed ||
      hasOpenRefundRequest(signup)
  ).length;

export const waitlistOnSquad = (signups = [], squadId) => {
  if (!squadId) return [];
  return (signups || [])
    .filter(
      (row) =>
        row.status === "waitlisted" &&
        (row.squadId || row.squadID) === squadId
    )
    .sort(
      (a, b) =>
        new Date(a.createdAt || a.created) - new Date(b.createdAt || b.created)
    );
};

export const waitlistPosition = (signup, signups = []) => {
  if (signup?.status !== "waitlisted") return null;
  if (signup.waitlistPosition != null && signup.waitlistPosition !== "") {
    const n = Number(signup.waitlistPosition);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  const sameSquad = waitlistOnSquad(
    signups,
    signup.squadId || signup.squadID
  );
  const index = sameSquad.findIndex((row) => row.id === signup.id);
  return index < 0 ? null : index + 1;
};

export const isNextOnWaitlist = (signup, signups = []) =>
  waitlistPosition(signup, signups) === 1;

export const waitlistPositionLabel = (signup, signups = []) => {
  const position = waitlistPosition(signup, signups);
  if (!position) return signup?.status === "waitlisted" ? "Waitlist" : null;
  return `Waitlist · position ${position}`;
};

export const bowlerNames = (signup) => {
  const bowlers = signup?.signupBowlers || signup?.bowlers || [];
  const names = bowlers.map((bowler) => bowler.name).filter(Boolean);
  return names.join(", ") || signup?.email || "Signup";
};
